import * as React from 'react';
import { Button, Checkbox, Input, TDeviceType, Tooltip } from '@cian/ui-kit';

import { Dialog } from '../dialog/dialog';
import { validateEmail } from '../../utils/validateEmail';
import { connect } from 'react-redux';
import { IUser } from '../../types/user';
import { ISubscribeToCommentsResponse } from '../../containers/post_card/post_card';
import { trackRegistration, trackShow, trackSubscribeWithNews } from './tracking';

import { disableScroll, enableScroll } from '../../../app/helpers/scroll';

import s from './dialogs.css';

interface IProps {
  shown: boolean;
  user: IUser;
  onClose(): void;
  onDigestSubmit(email: string, sub: boolean, isNews?: boolean): Promise<ISubscribeToCommentsResponse | void> | void;
  onDigestReset(): void;
  isSubscribed: boolean;
  error?: string;
  email: string;
  deviceType: TDeviceType;
}

const fakeEmailRex = /@social\.cian\.ru$/;

const renderLicense = () => (
  <div className={s['c-digest-form-license']}>
    <span>При подписке вы принимаете условия </span>
    <a
      target="_blank"
      href="//www.cian.ru/help/about/rules/"
      className={s['c-digest-form-license-link']}
      rel="noreferrer"
    >
      Пользовательского соглашения
    </a>
    <span> и </span>
    <a
      target="_blank"
      href="//www.cian.ru/help/about/36526/"
      className={s['c-digest-form-license-link']}
      rel="noreferrer"
    >
      Политики конфиденциальности
    </a>
  </div>
);

export const SubscribeDialog: React.FC<IProps> = ({
  shown,
  user,
  onClose,
  onDigestSubmit,
  onDigestReset,
  isSubscribed,
  error,
  email,
  deviceType,
}) => {
  const [value, setValue] = React.useState(
    email && !fakeEmailRex.test(email) ? email : '',
  );
  const [emailValid, setEmailValid] = React.useState(true);
  const [isNews, setIsNews] = React.useState(true);
  const [scrollPosition, setScrollPosition] = React.useState<number | undefined>(undefined);

  const prevShownRef = React.useRef(shown);

  React.useEffect(() => {
    const wasShown = prevShownRef.current;
    prevShownRef.current = shown;

    if (shown && shown !== wasShown) {
      setScrollPosition(window.pageYOffset);
      disableScroll();
      trackShow();
    } else {
      enableScroll();
      if (scrollPosition !== undefined) {
        window.scrollTo(0, scrollPosition);
      }
    }
  }, [shown, scrollPosition]);

  const submitEmail = () => {
    if (validateEmail(value)) {
      const digestResponse = onDigestSubmit(value, isNews);

      if (digestResponse) {
        digestResponse.then(res => {
          const attributes = res && res.data && res.data.attributes;

          if (attributes) {
            if (isNews) {
              trackSubscribeWithNews();
            }
            if (attributes.password) {
              trackRegistration();
            }
          }
        });
      }
    } else {
      setEmailValid(false);
    }
  };

  const renderForm = () => (
    <div className={`${s['c-digest-form']}`}>
      <h2 className={`${s['sub-dialog-content-header']} ${s['mobile-content']}`}>Подписка на комментарии</h2>
      <div className={`${s['sub-dialog-content-text']} ${s['mobile-content']}`}>
        Мы будем присылать вам на почту все новые комментарии
        <span className={s['break-mobile-hidden']}>
          <br />
        </span>{' '}
        к этой публикации
      </div>
      <div className={s['c-digest-form-container']}>
        <Tooltip
          placement="bottom"
          arrow={true}
          title={emailValid ? '' : 'Укажите email'}
          open={!emailValid}
          onClose={() => setEmailValid(true)}
        >
          <Input
            onChange={(_, newValue: string) => {
              setValue(newValue);
              setEmailValid(true);
            }}
            invalid={!emailValid}
            value={value}
            disabled={!!email}
            placeholder="Ваша электронная почта"
            autoFocus={true}
          />
        </Tooltip>
        <Button
          theme="fill_primary"
          size={deviceType === 'phone' ? 'M' : 'XS'}
          onClick={() => submitEmail()}
        >
          Подписаться
        </Button>
        <Checkbox
          label="Хочу получать новости Cian.ru"
          onChange={(_, val) => setIsNews(val)}
          checked={isNews}
        />
        {!user.userIdentifications && renderLicense()}
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div>
      <span className={s['c-digest-success-header']}>Подписка оформлена!</span>
      <span className={s['c-digest-success-text']}>
        Мы будем присылать письма на
        <span className={s['c-digest-email']}>{value}</span>
      </span>
    </div>
  );

  const renderFail = () => (
    <div>
      <span className={`${s['c-digest-fail-text']} ${s['desktop-content']}`}>
        <b>Что-то пошло не так, попробуйте подписаться еще раз</b>
      </span>
      <h2
        className={`
            ${s['sub-dialog-content-header']}
            ${s['c-digest-fail-text']}
            ${s['mobile-content']}`}
      >
        Что-то пошло не так...
      </h2>
      <div className={`${s['sub-dialog-fail-text']} ${s['mobile-content']}`}>Попробуйте подписаться еще раз</div>
      <Button
        theme="fill_error_primary"
        size={deviceType === 'phone' ? 'M' : 'XS'}
        onClick={() => onDigestReset()}
        fullWidth={true}
      >
        Повторить
      </Button>
    </div>
  );

  return (
    <Dialog
      content={
        <div className={s['sub-dialog-content']}>
          <h2 className={`${s['sub-dialog-content-header']} ${s['desktop-content']}`}>Подписка на комментарии</h2>
          <div className={`${s['sub-dialog-content-text']} ${s['desktop-content']}`}>
            Мы будем присылать вам на почту все новые комментарии
            <span className={s['break-mobile-hidden']}>
              <br />
            </span>{' '}
            к этой публикации
          </div>
          <div className={s['sub-dialog-content-controls']}>
            {error ? renderFail() : isSubscribed ? renderSuccess() : renderForm()}
          </div>
        </div>
      }
      shown={shown}
      onClose={() => onClose()}
    />
  );
};

export const SubscribeDialogContainer = connect<
  Pick<IProps, 'user'>,
  {},
  Pick<
    IProps,
    'shown' | 'onClose' | 'onDigestSubmit' | 'onDigestReset' | 'isSubscribed' | 'error' | 'email' | 'deviceType'
  >
>((state: { user: IUser }) => ({
  user: state.user,
}))(SubscribeDialog);
